import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import api, { API_BASE_URL } from '../helpers/api';
import { deleteMedia, uploadMultipleMedia } from '../helpers/upload';
import { getSingleEvent } from '../helpers/events';

const EventEdit = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState('');
	const [submitDisabled, setSubmitDisabled] = useState(false);
	const editorRef = useRef(null);

	const [formData, setFormData] = useState({
		name: '',
		description: '',
		location: '',
		venueName: '',
		url: '',
		startTime: '',
		endTime: '',
		images: []
	});

	useEffect(() => {
		if (id !== 'new') {
			fetchEvent();
		} else {
			setIsLoading(false);
		}
	}, [id]);

	const fetchEvent = async () => {
		try {
			// Format dates helper function
			const formatDate = (dateString) => {
				if (!dateString) return '';
				const date = new Date(dateString);
				return new Date(date.getTime() - (date.getTimezoneOffset() * 60000))
					.toISOString()
					.slice(0, 16);
			};

			const response = await api.get(`/events/${id}?populate=*`);
			const data = response.data.data;
			
			setFormData({
				name: data.name || '',
				description: data.description || '',
				location: data.location || '',
				venueName: data.venueName || '',
				url: data.url || '',
				startTime: formatDate(data.startTime),
				endTime: formatDate(data.endTime),
				images: data.images || []
			});
		} catch (err) {
			console.error('Erreur lors du chargement de l\'évènement:', err);
			setError('Erreur lors du chargement de l\'évènement');
		} finally {
			setIsLoading(false);
		}
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));
	};

	const stripSystemFields = (data) => {
		if (!data) return data;

		// Fields to strip completely
		const fieldsToStrip = ['id', 'documentId', 'createdAt', 'updatedAt', 'publishedAt'];

		// Fields where we need to keep the id (relations)
		const relationFields = ['images'];

		if (Array.isArray(data)) {
			return data.map(item => stripSystemFields(item));
		}

		if (typeof data === 'object') {
			const cleaned = {};

			for (const [key, value] of Object.entries(data)) {
				// Skip system fields
				if (fieldsToStrip.includes(key)) continue;

				// Handle relation fields
				if (relationFields.includes(key)) {
					if (Array.isArray(value)) {
						// Handle array relations (many-to-many)
						cleaned[key] = value.map(item => ({ id: item.id }));
					} else if (value && typeof value === 'object') {
						// Handle single relations (one-to-one, many-to-one)
						cleaned[key] = { id: value.id };
					} else if (typeof value === 'number') {
						// If value is just a number, convert to relation object
						cleaned[key] = { id: value };
					} else {
						cleaned[key] = value;
					}
					continue;
				}

				// Handle regular fields recursively
				if (Array.isArray(value)) {
					cleaned[key] = value.map(item =>
						typeof item === 'object' && item !== null ? stripSystemFields(item) : item
					);
				} else if (typeof value === 'object' && value !== null) {
					cleaned[key] = stripSystemFields(value);
				} else {
					cleaned[key] = value;
				}
			}

			return cleaned;
		}

		return data;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');
		setSubmitDisabled(true);
		let newId;

		try {
			// Create a copy of the form data to update
			let updatedFormData = { ...formData };

			// Get the current editor content
			if (editorRef.current) {
				updatedFormData.description = editorRef.current.getContent();
			}

			// Format dates for API
			const formatDateTime = (dateString) => {
				if (!dateString) return null;
				return dateString.length === 16 ? `${dateString}:00` : dateString;
			};

			// Prepare data for API
			const cleanedData = stripSystemFields({
				name: updatedFormData.name,
				description: updatedFormData.description,
				location: updatedFormData.location,
				venueName: updatedFormData.venueName,
				url: updatedFormData.url,
				startTime: formatDateTime(updatedFormData.startTime),
				endTime: formatDateTime(updatedFormData.endTime),
				images: updatedFormData.images
			});

			if (id === 'new') {
				// Create new event
				const response = await api.post('/events', {
					data: cleanedData
				});
				
				newId = response.data.data.id;
			} else {
				// Update existing event
				await api.put(`/events/${id}`, {
					data: cleanedData
				});
			}
		} catch (err) {
			console.error('Erreur lors de la sauvegarde:', err);
			setError('Erreur lors de la sauvegarde');
			setSubmitDisabled(false);
		} finally {
			alert(id === 'new' ? 'Ajouté avec succès' : 'Modifié avec succès');
			setSubmitDisabled(false);
			if (id === 'new') {
				navigate(`/events/${newId}`);
			}
		}
	};

	const handleImageUpload = async (e) => {
		const files = Array.from(e.target.files);

		try {
			const uploadedImages = await uploadMultipleMedia(files);
			
			setFormData(prev => ({
				...prev,
				images: [...prev.images, ...uploadedImages]
			}));
		} catch (err) {
			console.error('Erreur lors du téléchargement des images:', err);
			setError('Erreur lors du téléchargement des images');
		}
	};

	const handleImageDelete = async (imageId) => {
		try {
			await deleteMedia(imageId);
			setFormData(prev => ({
				...prev,
				images: prev.images.filter(img => img.id !== imageId)
			}));
		} catch (err) {
			console.error('Erreur lors de la suppression de l\'image:', err);
			setError('Erreur lors de la suppression de l\'image');
		}
	};

	if (isLoading) {
		return (
			<div className="text-center py-5">
				<div className="spinner-border text-primary" role="status">
					<span className="visually-hidden">Chargement...</span>
				</div>
			</div>
		);
	}

	return (
		<div className="container-fluid px-4">
			<div className="d-flex justify-content-between align-items-center mb-4">
				<h2>
					{id === 'new' ? 'Nouvel évènement' : 'Modifier l\'évènement'}
				</h2>
				<button
					className="btn btn-outline-secondary"
					onClick={() => navigate('/events')}
				>
					Retour à la liste
				</button>
			</div>

			{error && (
				<div className="alert alert-danger" role="alert">
					{error}
				</div>
			)}

			<div className="card shadow-sm">
				<div className="card-body">
					<form onSubmit={handleSubmit}>
						<div className="row mb-4">
							<div className="col-md-8">
								<div className="mb-3">
									<label className="form-label">Nom</label>
									<input
										type="text"
										className="form-control"
										name="name"
										value={formData.name}
										onChange={handleChange}
										required
									/>
								</div>

								<div className="mb-3">
									<label className="form-label">Description</label>
									<Editor
										apiKey="votre-clé-api-tinymce"
										onInit={(evt, editor) => editorRef.current = editor}
										initialValue={formData.description}
										init={{
											height: 400,
											menubar: false,
											plugins: [
												'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
												'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
												'insertdatetime', 'media', 'table', 'preview', 'help', 'wordcount'
											],
											toolbar: 'undo redo | blocks | ' +
												'bold italic forecolor | alignleft aligncenter ' +
												'alignright alignjustify | bullist numlist outdent indent | ' +
												'removeformat | help',
											content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
										}}
									/>
								</div>

								<div className="mb-3">
									<label className="form-label">Adresse</label>
									<div className="row">
										<div className="col-md-7">
											<input
												type="text"
												className="form-control mb-2"
												name="location"
												value={formData.location}
												onChange={handleChange}
												placeholder="Adresse"
												required
											/>
										</div>
										<div className="col-md-5">
											<input
												type="text"
												className="form-control"
												name="venueName"
												value={formData.venueName}
												onChange={handleChange}
												placeholder="Lieu"
												required
											/>
										</div>
									</div>
								</div>
							</div>

							<div className="col-md-4">
								<div className="mb-3">
									<label className="form-label">Images</label>
									<input
										type="file"
										className="form-control mb-2"
										multiple
										accept="image/*"
										onChange={handleImageUpload}
									/>
									<div className="row g-2">
										{formData.images.map((image, index) => (
											<div key={image.id} className="col-6">
												<div className="position-relative">
													<img
														src={API_BASE_URL + image.url}
														alt=""
														className="img-thumbnail"
														style={{ width: '100%', height: '120px', objectFit: 'cover' }}
													/>
													<button
														type="button"
														className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1"
														onClick={() => handleImageDelete(image.id)}
													>
														<i className="bi bi-trash"></i>
													</button>
												</div>
											</div>
										))}
									</div>
								</div>

								<div className="mb-3">
									<label className="form-label">Date et horaires</label><br />
									<small>Date de début</small>
									<input
										type="datetime-local"
										className="form-control mb-2"
										name="startTime"
										value={formData.startTime}
										onChange={handleChange}
									/>
									<small>Date de fin</small>
									<input
										type="datetime-local"
										className="form-control mb-2"
										name="endTime"
										value={formData.endTime}
										onChange={handleChange}
									/>
									<label className="form-label">Autre</label>
									<input
										type="url"
										className="form-control"
										name="url"
										value={formData.url}
										onChange={handleChange}
										placeholder="Site web"
									/>
								</div>
							</div>
						</div>

						<div className="d-flex justify-content-end gap-2">
							<button
								type="button"
								className="btn btn-outline-secondary"
								onClick={() => navigate('/events')}
							>
								Annuler
							</button>
							<button
								type="submit"
								className="btn btn-primary"
								disabled={submitDisabled}
							>
								{submitDisabled ? (
									<>
										<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
										Enregistrement...
									</>
								) : (
									'Enregistrer'
								)}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
};

export default EventEdit;